import { useEffect, useMemo, useState } from 'react';

const SPRITE_SRC = '/beary-explorer-clean.png';
const SHEET_COLS = 8;
const SHEET_ROWS = 9;
const COUNTRY_WIZARD_PATH = '/country-wizards/';

export const COUNTRY_WIZARD_SPRITES = {
  Australia: 'australia-outback-alpha.png',
  Brazil: 'brazil-carnival-feathers-alpha.png',
  Canada: 'canada-knit-maple-alpha.png',
  Egypt: 'egypt-pharaoh-gold-alpha.png',
  France: 'france-beret-salute-alpha.png',
  Germany: 'germany-bavaria-alpha.png',
  India: 'india-traditional-alpha.png',
  Japan: 'japan-kimono-alpha.png',
  Mexico: 'mexico-sombrero-alpha.png',
  'United States': 'united-states-cap-hoodie-alpha.png',
};

const COUNTRY_ALIASES = {
  USA: 'United States',
  US: 'United States',
  'United States of America': 'United States',
};

// Static (single-frame) costume images generated by
// backend/scripts/generate-wizard-costumes.mjs. Any file named
// `<country-slug>-static.png` in /country-wizards/ is picked up
// automatically — no code change needed per country.
const slugify = (name = '') =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const BEARY_ANIMS = {
  idle: { row: 0, frames: 6, fps: 7 },
  waving: { row: 3, frames: 4, fps: 9 },
  jumping: { row: 4, frames: 5, fps: 11 },
  failed: { row: 5, frames: 8, fps: 6 },
  waiting: { row: 6, frames: 6, fps: 5 },
  questing: { row: 7, frames: 6, fps: 10 },
  reading: { row: 8, frames: 6, fps: 7 },
};

export default function BearySprite({
  anim = BEARY_ANIMS.idle,
  width = 96,
  height = 110,
  countryName,
  style,
  fallbackStyle,
}) {
  const [frame, setFrame] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [failedSrcs, setFailedSrcs] = useState(() => new Set());

  const source = useMemo(() => {
    const resolvedCountry = COUNTRY_ALIASES[countryName] || countryName;
    const countryFile = COUNTRY_WIZARD_SPRITES[resolvedCountry];
    const countrySrc = countryFile ? `${COUNTRY_WIZARD_PATH}${countryFile}` : null;

    // 1) Hand-made 8-frame costume sheet
    if (countrySrc && !failedSrcs.has(countrySrc)) {
      return {
        src: countrySrc,
        cols: 8,
        rows: 1,
        anim: { row: 0, frames: 8, fps: anim.fps },
        isDefault: false,
        isStatic: false,
      };
    }

    // 2) AI-generated static costume (gentle CSS bob instead of frames)
    if (resolvedCountry) {
      const staticSrc = `${COUNTRY_WIZARD_PATH}${slugify(resolvedCountry)}-static.png`;
      if (!failedSrcs.has(staticSrc)) {
        return {
          src: staticSrc,
          cols: 1,
          rows: 1,
          anim: { row: 0, frames: 1, fps: 1 },
          isDefault: false,
          isStatic: true,
        };
      }
    }

    // 3) Default Beary sheet
    return {
      src: SPRITE_SRC,
      cols: SHEET_COLS,
      rows: SHEET_ROWS,
      anim,
      isDefault: true,
      isStatic: false,
    };
  }, [anim, countryName, failedSrcs]);

  useEffect(() => {
    setFrame(0);
  }, [source.anim.row, source.anim.frames, source.src]);

  useEffect(() => {
    setLoaded(false);
  }, [source.src]);

  useEffect(() => {
    setFailedSrcs(new Set());
  }, [countryName]);

  useEffect(() => {
    const id = setInterval(
      () => setFrame((current) => (current + 1) % source.anim.frames),
      Math.round(1000 / source.anim.fps)
    );
    return () => clearInterval(id);
  }, [source.anim.frames, source.anim.fps]);

  const metrics = useMemo(() => {
    const displayFrameHeight = height;
    if (source.isStatic) {
      // Square AI image — fit without distortion, anchored to the ground
      return { displayFrameHeight, bgSize: 'contain', bgPos: 'center bottom' };
    }
    return {
      displayFrameHeight,
      bgSize: `${source.cols * width}px ${source.rows * displayFrameHeight}px`,
      bgPos: `-${frame * width}px -${source.anim.row * displayFrameHeight}px`,
    };
  }, [frame, height, source.anim.row, source.cols, source.rows, source.isStatic, width]);

  return (
    <>
      <img
        key={source.src}
        src={source.src}
        alt=""
        style={{ display: 'none' }}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(false);
          if (!source.isDefault) {
            setFailedSrcs(prev => new Set(prev).add(source.src));
          }
        }}
      />
      {loaded ? (
        <div
          aria-hidden="true"
          style={{
            width,
            height: metrics.displayFrameHeight,
            backgroundImage: `url(${source.src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: metrics.bgSize,
            backgroundPosition: metrics.bgPos,
            filter: 'drop-shadow(0 12px 22px rgba(56, 27, 5, 0.25))',
            ...(source.isStatic ? { animation: 'orboot-bob 3.2s ease-in-out infinite' } : {}),
            ...style,
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width,
            height: metrics.displayFrameHeight,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            fontSize: Math.max(48, width * 0.68),
            filter: 'drop-shadow(0 12px 22px rgba(56, 27, 5, 0.25))',
            ...fallbackStyle,
          }}
        >
          🐻
        </div>
      )}
    </>
  );
}
